import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { betterResult, type ExamAttempt, type ExamResult } from './attempt.js';
import { mergeResults } from './results.js';

/**
 * Exam sittings on this device, per signed-in user. Persisted to
 * localStorage so closing the app keeps every answer and the clock where
 * it stopped (Tornike 2026-09-25: no pause button, but closing the app
 * pauses; it continues when they come back).
 *
 * Keyed by user id: two people sharing a phone must never inherit each
 * other's half-finished test.
 *
 * Finished results also live on the server (exam_results, 0022): each one
 * is sent when it is recorded (hooks.ts useSaveExamResult) and the server's
 * list is merged back in (mergeRemoteResults) so results follow the account.
 * The sitting in progress stays on the device.
 */

interface UserExams {
  /** testId → the sitting in progress. */
  active: Record<string, ExamAttempt>;
  /** testId → best and latest finished result. */
  results: Record<string, { best: ExamResult; last: ExamResult }>;
  /** The "how this differs from the real exam" notice was shown once. */
  introSeen: boolean;
}

interface ExamStoreState {
  byUser: Record<string, UserExams>;
  saveAttempt: (userKey: string, attempt: ExamAttempt) => void;
  discardAttempt: (userKey: string, testId: string) => void;
  recordResult: (userKey: string, result: ExamResult) => void;
  /** Merge sittings fetched from the server into best/last (no-op when nothing changes). */
  mergeRemoteResults: (userKey: string, remote: readonly ExamResult[]) => void;
  markIntroSeen: (userKey: string) => void;
}

const EMPTY: UserExams = { active: {}, results: {}, introSeen: false };

function userOf(state: ExamStoreState, userKey: string): UserExams {
  return state.byUser[userKey] ?? EMPTY;
}

export const useExamStore = create<ExamStoreState>()(
  persist(
    (set) => ({
      byUser: {},
      saveAttempt: (userKey, attempt) =>
        set((s) => {
          const u = userOf(s, userKey);
          return {
            byUser: { ...s.byUser, [userKey]: { ...u, active: { ...u.active, [attempt.testId]: attempt } } },
          };
        }),
      discardAttempt: (userKey, testId) =>
        set((s) => {
          const u = userOf(s, userKey);
          const active = { ...u.active };
          delete active[testId];
          return { byUser: { ...s.byUser, [userKey]: { ...u, active } } };
        }),
      recordResult: (userKey, result) =>
        set((s) => {
          const u = userOf(s, userKey);
          const active = { ...u.active };
          delete active[result.testId];
          const prev = u.results[result.testId];
          return {
            byUser: {
              ...s.byUser,
              [userKey]: {
                ...u,
                active,
                results: {
                  ...u.results,
                  [result.testId]: { best: betterResult(prev?.best, result), last: result },
                },
              },
            },
          };
        }),
      mergeRemoteResults: (userKey, remote) =>
        set((s) => {
          const u = userOf(s, userKey);
          const results = mergeResults(u.results, remote);
          if (results === u.results) return {};
          return { byUser: { ...s.byUser, [userKey]: { ...u, results } } };
        }),
      markIntroSeen: (userKey) =>
        set((s) => ({ byUser: { ...s.byUser, [userKey]: { ...userOf(s, userKey), introSeen: true } } })),
    }),
    {
      name: 'fluent-exam-v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ byUser: s.byUser }),
    },
  ),
);

/** Read one user's slice (stable EMPTY when they have none). */
export function useUserExams(userKey: string): UserExams {
  return useExamStore((s) => s.byUser[userKey] ?? EMPTY);
}
